import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar, MapPin, Video, Users, QrCode, Check } from 'lucide-react';
import { format } from 'date-fns';
import { useRSVPEvent, useCancelRSVP, EventWithAttendees } from '@/hooks/useEvents';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useMembershipContext } from '@/components/layout/AppLayout';
import type { Database } from '@/integrations/supabase/types';

type EventLocationType = Database['public']['Enums']['event_location_type'];

interface EventCardProps {
  event: EventWithAttendees;
}

const LOCATION_TYPE_CONFIG: Record<EventLocationType, { label: string; icon: React.ElementType }> = {
  virtual: { label: 'Virtual', icon: Video },
  in_person: { label: 'In Person', icon: MapPin },
  hybrid: { label: 'Hybrid', icon: Users },
};

export function EventCard({ event }: EventCardProps) {
  const [showAttendees, setShowAttendees] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { isPending } = useMembershipContext();
  const rsvpEvent = useRSVPEvent();
  const cancelRSVP = useCancelRSVP();

  const isAttending = event.attendees.some(a => a.user_id === user?.id);
  const attendeeCount = event.attendees.length;
  const isFull = !!event.max_attendees && attendeeCount >= event.max_attendees;
  const locationConfig = LOCATION_TYPE_CONFIG[event.location_type];
  const LocationIcon = locationConfig.icon;

  const handleRSVP = async () => {
    try {
      if (isAttending) {
        await cancelRSVP.mutateAsync(event.id);
        toast({ title: 'RSVP cancelled' });
      } else {
        if (isPending) {
          toast({ title: 'Complete your profile', description: 'RSVP unlocks after approval.' });
          return;
        }
        if (isFull) {
          toast({ title: 'Event full', description: 'No more spots are available.' });
          return;
        }
        await rsvpEvent.mutateAsync(event.id);
        toast({ title: "You're in!", description: 'See you there!' });
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <Card className="card-qbn hover:shadow-qbn-float transition-shadow overflow-hidden">
      {/* Color accent bar */}
      <div className="h-1 bg-gradient-primary" />
      
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg text-foreground">{event.title}</h3>
            <Badge variant="outline" className="mt-1">
              {event.cohort_tag === 'global' ? 'Global' : event.cohort_tag}
            </Badge>
          </div>
          <Badge variant="secondary" className="gap-1">
            <LocationIcon className="h-3 w-3" />
            {locationConfig.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {event.description && (
          <p className="text-sm text-muted-foreground">{event.description}</p>
        )}

        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 text-primary" />
            <span>{format(new Date(event.event_date), 'PPP p')}</span>
          </div>
          {event.location_address && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              <span>{event.location_address}</span>
            </div>
          )}
        </div>

        {/* Attendees Preview */}
        <Dialog open={showAttendees} onOpenChange={setShowAttendees}>
          <DialogTrigger asChild>
            <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <div className="flex -space-x-2">
                {event.attendees.slice(0, 5).map((attendee, i) => (
                  <Avatar key={i} className="h-6 w-6 border-2 border-card">
                    <SignedAvatarImage src={attendee.profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {attendee.profile?.full_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span>
                {attendeeCount} attending
                {event.max_attendees && ` / ${event.max_attendees} spots`}
              </span>
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Who's Attending</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {event.attendees.map((attendee) => (
                <div key={attendee.user_id} className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <SignedAvatarImage src={attendee.profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {attendee.profile?.full_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{attendee.profile?.full_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {attendee.profile?.company}
                    </p>
                  </div>
                  {attendee.checked_in && (
                    <Badge className="ml-auto badge-referral">
                      <Check className="h-3 w-3 mr-1" />
                      Checked in
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            onClick={handleRSVP}
            disabled={rsvpEvent.isPending || cancelRSVP.isPending || (!isAttending && isFull) || isPending}
            className={isAttending ? 'btn-qbn-outline flex-1' : 'btn-qbn-primary flex-1'}
          >
            {isAttending ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Going
              </>
            ) : (
              isPending ? 'Complete profile to RSVP' : isFull ? 'Event full' : 'RSVP'
            )}
          </Button>
          {event.location_type !== 'virtual' && (
            <Button variant="outline" size="icon">
              <QrCode className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
